// BeneAIReceiptCard — BeneAI-02.
//
// Rendered after Confirm / Cancel resolves a pending confirmation.
// Success carries an optional output summary; failed carries the
// error message; cancelled is a quiet acknowledgement so the
// transcript records the user's decision.

import { Check, Slash, X } from "lucide-react";
import { useMemo } from "react";

import type { BeneAIReceiptBlock } from "../types";


export function BeneAIReceiptCard({ block }: { block: BeneAIReceiptBlock }) {
  const meta = receiptMeta(block.outcome);
  const preview = useMemo(() => summarizeOutput(block), [block]);
  return (
    <div
      data-testid="beneai-receipt"
      data-outcome={block.outcome}
      data-tool-name={block.tool_name}
      className={[
        "self-stretch rounded-md border px-3 py-2 text-sm",
        meta.surface,
      ].join(" ")}
    >
      <div className="flex items-center gap-1.5">
        <meta.Icon
          size={14} aria-hidden="true" className={meta.iconClass}
        />
        <span className="font-medium text-text-primary">
          {block.summary || meta.fallback(block.tool_name)}
        </span>
      </div>
      {preview && (
        <div className="mt-1 whitespace-pre-wrap break-words text-xs text-text-secondary">
          {preview}
        </div>
      )}
      {block.outcome === "failed" && block.error_message && (
        <div className="mt-1 text-xs text-ember-ink">
          {block.error_message}
        </div>
      )}
      {block.undo_available && block.outcome === "success" && (
        <div
          data-testid="beneai-receipt-undo-available"
          className="mt-1 text-xs text-text-tertiary"
        >
          Undo available — BeneAI-02 reports availability only; the
          undo action ships in a later chunk.
        </div>
      )}
    </div>
  );
}


function receiptMeta(outcome: BeneAIReceiptBlock["outcome"]) {
  if (outcome === "success") {
    return {
      Icon: Check,
      iconClass: "text-teal",
      surface: "border-sage-tint bg-canvas",
      fallback: (name: string) => `Done — ${humanize(name)}.`,
    };
  }
  if (outcome === "cancelled") {
    return {
      Icon: Slash,
      iconClass: "text-text-tertiary",
      surface: "border-border-subtle bg-canvas",
      fallback: (name: string) => `Cancelled — ${humanize(name)}.`,
    };
  }
  return {
    Icon: X,
    iconClass: "stroke-ember",
    surface: "border-ember-tint bg-canvas",
    fallback: (name: string) => `Couldn't ${humanize(name)}.`,
  };
}


function humanize(toolName: string): string {
  return toolName.replace(/_/g, " ");
}


function summarizeOutput(block: BeneAIReceiptBlock): string | null {
  if (block.outcome !== "success") return null;
  const o = block.output_json;
  if (!o || typeof o !== "object") return null;
  const obj = o as Record<string, unknown>;
  for (const key of [
    "account_id", "commitment_id", "touchpoint_id",
    "playbook_application_id", "plan_year_id",
  ]) {
    if (typeof obj[key] === "string") return `${key}: ${obj[key]}`;
  }
  if (typeof obj.legal_name === "string") return obj.legal_name;
  if (typeof obj.title === "string") return obj.title;
  return null;
}
